'use client'

import { useQueryClient } from '@tanstack/react-query'
import { AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps'
import { AnimatePresence, motion, Variants } from 'motion/react'
import { useLocale } from 'next-intl'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetCitiesMapQuery } from '@/api/cities/get-cities-map-query'
import {
    getCityRestaurantsMapQueryOptions,
    toGoogleMapsRestaurants,
} from '@/api/cities/get-city-restaurants-map-query'
import { RESTAURANT_ZOOM_LEVEL_THRESHOLD } from '@/components/map/utils/neotaste-map-constants'
import {
    boundsToGoogleLatLngBounds,
    coordinateToGoogleLatLng,
} from '@/components/map/utils/neotaste-map-helpers'
import {
    IGoogleMapsCity,
    IGoogleMapsRestaurant,
    INeotasteMapProps,
} from '@/components/map/utils/neotaste-map-types'
import { useCitiesFinderContext } from '@/features/cities/cities-finder/cities-finder-context'
import { MapLabel } from '@/features/cities/map-label'
import { MapConsentGate } from '@/features/gdpr/map-consent-gate'
import { SvgMapNeedle } from '@/icons/svg-map-needle'
import { useRouter } from '@/setup/i18n/use-router'
import { parseSettled } from '@/utils/functions/parseSettled'
import { throttle } from '@/utils/functions/throttle'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { useMobile } from '@/utils/window/use-mobile'

const RawNeotasteMap = memo(
    ({
        className,
        style,
        withInitialDelay,
        zoomMobile,
        zoomDesktop,
        focusOnCitySlug,
        centerDesktop,
        centerMobile,
        visibleRestaurants: forcedVisibleRestaurants,
        additionalMarker,
        markerSize = 36,
        disableRestaurantClicks = false,
    }: INeotasteMapProps) => {
        // --- STATE ---

        const router = useRouter()
        const isMobile = useMobile()

        const map = useMap()
        const locale = useLocale()

        const { hoveredCitySlug } = useCitiesFinderContext()

        const hasSyncedFocusCityRef = useRef(false)

        const [hoveredMarker, setHoveredMarker] = useState<string>()
        const [shouldRenderMap, setShouldRenderMap] = useState(!withInitialDelay)
        const [, setMapIdle] = useState(true)
        const [visibleRestaurants, setVisibleRestaurants] = useState<IGoogleMapsRestaurant[]>(
            forcedVisibleRestaurants ?? []
        )

        const qc = useQueryClient()
        const { data: cities } = useGetCitiesMapQuery()

        // --- CALLBACKS ---

        const calculateVisibleRestaurants = useCallback(
            (zoom: number, loadedRestaurants: IGoogleMapsRestaurant[]) => {
                return loadedRestaurants.filter((restaurant) => {
                    if (!restaurant.zoomLevel) return true

                    return restaurant.zoomLevel <= zoom
                })
            },
            []
        )

        const makeOnRestaurantClick = useCallback(
            (restaurant: IGoogleMapsRestaurant) => () => {
                if (!restaurant.citySlug || disableRestaurantClicks) return

                router.push({
                    pattern: '/restaurants/[citySlug]/[restaurantSlug]',
                    params: {
                        citySlug: restaurant.citySlug,
                        restaurantSlug: restaurant.slug,
                    },
                })
            },
            [router, disableRestaurantClicks]
        )

        const makeOnCityClick = useCallback(
            (city: IGoogleMapsCity) => () => {
                if (city.latLngBounds && map) {
                    setMapIdle(false)
                    map.fitBounds(city.latLngBounds)
                }
            },
            [map]
        )

        // --- MEMOIZED DATA ---

        const googleMapsCities = useMemo((): IGoogleMapsCity[] => {
            if (cities && map) {
                return cities.data.map((city) => ({
                    ...city,
                    latLngBounds: city.bounds
                        ? new google.maps.LatLngBounds(boundsToGoogleLatLngBounds(city.bounds))
                        : undefined,
                }))
            }

            return []
        }, [map, cities])

        const onBoundsChanged = useMemo(() => {
            // Throttle the function to avoid calling it too often (e.g. when the map is moved around quickly)
            return throttle((map: google.maps.Map) => {
                const mapBounds = map.getBounds()
                const zoom = map.getZoom()

                if (!googleMapsCities || !map || forcedVisibleRestaurants || !mapBounds || !zoom)
                    return

                if (zoom < RESTAURANT_ZOOM_LEVEL_THRESHOLD) {
                    setVisibleRestaurants([])

                    return
                }

                const visibleCities = googleMapsCities.filter((city) =>
                    city.latLngBounds?.intersects(mapBounds)
                )

                const visibleCityRestaurantsPromises = visibleCities.map((visibleCity) =>
                    qc
                        .fetchQuery(getCityRestaurantsMapQueryOptions(visibleCity.slug, locale))
                        .then((response) => toGoogleMapsRestaurants(response.data))
                )

                // Parse the Promise.allSettled result and set the visible restaurants
                Promise.allSettled(visibleCityRestaurantsPromises)
                    .then(parseSettled)
                    .then((loadedGoogleMapsRestaurant) => {
                        const loadedRestaurants = loadedGoogleMapsRestaurant.flat()

                        const visibleRestaurants = calculateVisibleRestaurants(
                            zoom,
                            loadedRestaurants
                        )

                        setVisibleRestaurants(visibleRestaurants)
                    })
            }, 500)
        }, [calculateVisibleRestaurants, googleMapsCities, qc, locale, forcedVisibleRestaurants])

        // --- EFFECTS ---

        useEffect(() => {
            if (!focusOnCitySlug || hasSyncedFocusCityRef.current || !googleMapsCities) return

            const cityWithBounds = googleMapsCities.find((city) => city.slug === focusOnCitySlug)

            if (!cityWithBounds) return

            hasSyncedFocusCityRef.current = true

            makeOnCityClick(cityWithBounds)()
        }, [focusOnCitySlug, googleMapsCities, makeOnCityClick])

        useEffect(() => {
            if (shouldRenderMap) {
                return
            }

            setTimeout(() => {
                setShouldRenderMap(true)
            }, 2500)
        }, [shouldRenderMap])

        useEffect(() => {
            if (!map) return

            const boundsListener = map.addListener('bounds_changed', () => onBoundsChanged(map))
            const idleListener = map.addListener('idle', () => setMapIdle(true))

            return () => {
                google.maps.event.removeListener(boundsListener)
                google.maps.event.removeListener(idleListener)
            }
        }, [map, onBoundsChanged])

        // --- RENDER ---

        return (
            <div style={style} className={cn('relative touch-none md:touch-auto', className)}>
                {run(() => {
                    if (!shouldRenderMap) return null

                    return (
                        <Map
                            defaultCenter={isMobile ? centerMobile : centerDesktop}
                            defaultZoom={isMobile ? zoomMobile : zoomDesktop}
                            mapTypeControl={false}
                            streetViewControl={false}
                            isFractionalZoomEnabled={false}
                            zoomControl={false}
                            fullscreenControl={false}
                            mapId="f69edb14685f6666"
                            className="absolute inset-0 focus:outline-hidden"
                        >
                            {run(() => {
                                if (!cities?.data.length || visibleRestaurants.length) return []

                                return googleMapsCities.map((city) => {
                                    const isHoveredOnMarker = hoveredMarker === city.slug
                                    const isHoveredFromList = hoveredCitySlug === city.slug

                                    const isHovered = isHoveredOnMarker || isHoveredFromList

                                    return (
                                        <AdvancedMarker
                                            key={'neotaste-map' + city.slug}
                                            position={coordinateToGoogleLatLng(city.center)}
                                            onClick={makeOnCityClick(city)}
                                            zIndex={isHovered ? 3 : city.label ? 1 : 0}
                                        >
                                            <motion.div
                                                initial={{ scale: isHoveredFromList ? 1.4 : 1 }}
                                                animate={{ scale: isHoveredFromList ? 1.4 : 1 }}
                                                whileHover={{ scale: 1.4 }}
                                                onMouseOver={() => setHoveredMarker(city.slug)}
                                                className="relative"
                                            >
                                                <SvgMapNeedle color="none" size={36} />
                                                <MapLabel
                                                    label={city.label}
                                                    className="absolute top-[-8px] right-[-28px] scale-[0.7]"
                                                />
                                            </motion.div>
                                        </AdvancedMarker>
                                    )
                                })
                            })}

                            {!!additionalMarker?.latLng && (
                                <AdvancedMarker
                                    position={additionalMarker.latLng}
                                    key={
                                        'neotaste-additional-map-marker' +
                                        additionalMarker.latLng.lat() +
                                        additionalMarker.latLng.lng()
                                    }
                                    zIndex={2}
                                >
                                    <SvgMapNeedle
                                        color="none"
                                        size={additionalMarker.size ?? markerSize}
                                    />
                                </AdvancedMarker>
                            )}

                            {visibleRestaurants.map((visibleRestaurant) => {
                                if (!visibleRestaurant.latLng) return null

                                return (
                                    <AdvancedMarker
                                        position={visibleRestaurant.latLng}
                                        key={'neotaste-map' + visibleRestaurant.uuid}
                                        onClick={makeOnRestaurantClick(visibleRestaurant)}
                                        zIndex={1}
                                    >
                                        <AnimatePresence initial={false}>
                                            <motion.div
                                                initial="initial"
                                                animate="idle"
                                                whileHover="hover"
                                                variants={variants}
                                                className="relative"
                                                transition={{
                                                    opacity: {
                                                        duration: 0.15,
                                                    },
                                                    scale: {
                                                        duration: 0.3,
                                                        type: 'spring',
                                                        bounce: 0.05,
                                                    },
                                                }}
                                            >
                                                <SvgMapNeedle color="none" size={markerSize} />

                                                <MapLabel
                                                    label={visibleRestaurant.label ?? ''}
                                                    className="absolute top-[-8px] right-[-28px] scale-[0.7]"
                                                />
                                            </motion.div>
                                        </AnimatePresence>
                                    </AdvancedMarker>
                                )
                            })}
                        </Map>
                    )
                })}
            </div>
        )
    }
)

RawNeotasteMap.displayName = 'RawNeotasteMap'

export const NeotasteMap = memo((props: INeotasteMapProps) => (
    <MapConsentGate className={props.className} style={props.style}>
        <RawNeotasteMap {...props} />
    </MapConsentGate>
))

NeotasteMap.displayName = 'NeotasteMap'

const variants: Variants = {
    initial: { scale: 0.2, opacity: 0 },
    idle: { scale: 1, opacity: 1 },
    hover: { scale: 1.4 },
}
